import fakeRestDataProvider from "ra-data-fakerest";
import type { GetListParams, GetListResult, RaRecord } from "ra-core";

import type { ConfigurationContextValue } from "../../root/ConfigurationContext";
import type { CrmDataProvider } from "../types";
import generateData from "./dataGenerator";
import type { Db, Job } from "./dataGenerator/types";
import { getCurrentUser } from "./authProvider";

export interface CreateFakeRestDataProviderOptions {
  db?: Db;
  latency?: number;
}

export const createDataProvider = ({
  db = generateData(),
  latency = 300,
}: CreateFakeRestDataProviderOptions = {}): CrmDataProvider => {
  const baseDataProvider = fakeRestDataProvider(db, true, latency);

  // Wrap getList to apply RLS filtering for sales_rep
  const getListWithRLS = async <RecordType extends RaRecord = RaRecord>(
    resource: string,
    params: GetListParams,
  ): Promise<GetListResult<RecordType>> => {
    const result = await baseDataProvider.getList<RecordType>(resource, params);

    // Apply RLS for jobs - sales reps can only see their assigned jobs
    if (resource === "jobs") {
      const user = getCurrentUser();
      if (user && user.role === "sales_rep") {
        const filteredData = result.data.filter(
          (job) => (job as unknown as Job).assigned_to === user.id,
        );
        return {
          data: filteredData,
          total: filteredData.length,
          pageInfo: result.pageInfo,
        };
      }
    }

    return result;
  };

  const dataProvider: CrmDataProvider = {
    ...baseDataProvider,
    getList: getListWithRLS,
    isInitialized: async (): Promise<boolean> => {
      const sales = await baseDataProvider.getList("sales", {
        filter: {},
        pagination: { page: 1, perPage: 1 },
        sort: { field: "id", order: "ASC" },
      });
      return sales.data.length > 0;
    },
    getConfiguration: async (): Promise<ConfigurationContextValue> => {
      const { data } = await baseDataProvider.getOne("configuration", {
        id: 1,
      });
      return (data?.config as ConfigurationContextValue) ?? {};
    },
    updateConfiguration: async (
      config: ConfigurationContextValue,
    ): Promise<ConfigurationContextValue> => {
      const { data: prev } = await baseDataProvider.getOne("configuration", {
        id: 1,
      });
      await baseDataProvider.update("configuration", {
        id: 1,
        data: { config },
        previousData: prev,
      });
      return config;
    },
  };

  return dataProvider;
};

export const dataProvider = createDataProvider();
